import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  HiOutlineDocumentReport,
  HiOutlineDownload,
  HiOutlineFilter,
  HiOutlineX,
  HiOutlineAcademicCap,
} from "react-icons/hi";
import { generateQLRReport, exportQLRReportExcel } from "../services/reportingApi";
import { fetchMasterData, fetchAssessmentYears } from "../services/masterDataApi";
import { useUserRole } from "../hooks/useUserRole";
import { fetchMyStates, fetchMyBlocks, fetchMyDistricts } from "../services/assignmentApi";
import SearchableDropdown from "../components/SearchableDropdown";

const RISK_COLORS = {
  Low: { bg: "bg-green-100", text: "text-green-800", border: "border-green-300" },
  Medium: { bg: "bg-yellow-100", text: "text-yellow-800", border: "border-yellow-300" },
  High: { bg: "bg-orange-100", text: "text-orange-800", border: "border-orange-300" },
  "Ultra High": { bg: "bg-red-100", text: "text-red-800", border: "border-red-300" },
};

function QLRReport() {
  const { role, userProfile } = useUserRole();
  const [states, setStates] = useState([]);
  const [pmBlocks, setPmBlocks] = useState([]);
  const [plDistricts, setPlDistricts] = useState([]);
  const [assessmentYears, setAssessmentYears] = useState([]);
  const [cycles, setCycles] = useState([]);

  const [filters, setFilters] = useState({
    state_id: "",
    assessment_year_id: "",
    cycle_id: "",
    district_id: "",
    block_id: "",
  });


  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Load states based on role
  useEffect(() => {
    if (!role) return;
    // ADMIN's State picker is a SearchableDropdown that fetches its own options.
    if (role === "SPL") {
      fetchMyStates().then((myStates) => {
        if (myStates && myStates.length > 0) {
          setStates(myStates);
          setFilters((prev) => ({ ...prev, state_id: String(myStates[0].id) }));
        }
      }).catch(() => {});
    } else if (role === "PL") {
      fetchMyDistricts().then((myDistricts) => {
        if (myDistricts && myDistricts.length > 0) {
          setPlDistricts(myDistricts);
          setFilters((prev) => ({ ...prev, state_id: String(myDistricts[0].state_id) }));
        }
      }).catch(() => {});
    } else if (role === "PM") {
      fetchMyBlocks().then((myBlocks) => {
        if (myBlocks && myBlocks.length > 0) {
          setPmBlocks(myBlocks);
          setFilters((prev) => ({ ...prev, state_id: String(myBlocks[0].state_id) }));
        }
      }).catch(() => {});
    } else if (role === "PO" && userProfile?.state_id) {
      setFilters((prev) => ({ ...prev, state_id: String(userProfile.state_id) }));
    }
  }, [role, userProfile]);

  // Assessment years don't depend on the state — fetch once, show once a
  // state is picked.
  const allYearsRef = useRef(null);
  useEffect(() => {
    if (filters.state_id) {
      if (!allYearsRef.current) {
        allYearsRef.current = fetchAssessmentYears()
          .then((res) => (res.success ? res.data || [] : []))
          .catch(() => { allYearsRef.current = null; return []; });
      }
      allYearsRef.current.then(setAssessmentYears);
    } else {
      setAssessmentYears([]);
      setCycles([]);
    }
  }, [filters.state_id]);

  // The Cycle dropdown is filled from the main report's `available_cycles`
  // (see loadReport) — the backend builds that list from state + year only,
  // before applying the cycle/district/block filters. This used to be a
  // second, identical generateQLRReport call.


  // Generate report
  const loadReport = useCallback(async () => {
    if (!filters.state_id) {
      setReportData(null);
      return;
    }
    setLoading(true);
    try {
      const payload = { state_id: Number(filters.state_id) };
      if (filters.district_id) payload.district_id = Number(filters.district_id);
      if (filters.block_id) payload.block_id = Number(filters.block_id);
      if (filters.assessment_year_id) payload.assessment_year_id = filters.assessment_year_id;
      if (filters.cycle_id) payload.cycle_id = filters.cycle_id;

      const res = await generateQLRReport(payload);
      if (res.success) {
        setReportData(res.data);
        setCycles(res.data?.available_cycles || []);
      }
    } catch (err) {
      console.error("QLR Report generation failed:", err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    if (filters.state_id) {
      loadReport();
    }
  }, [loadReport]);

  const handleFilterChange = (key, value) => {
    const next = { ...filters, [key]: value };
    if (key === "state_id") {
      next.assessment_year_id = "";
      next.cycle_id = "";
      next.district_id = "";
      next.block_id = "";
    }
    if (key === "assessment_year_id") {
      next.cycle_id = "";
    }
    if (key === "district_id") {
      next.block_id = "";
    }
    setFilters(next);
  };

  const clearFilters = () => {
    const base = { state_id: "", assessment_year_id: "", cycle_id: "", district_id: "", block_id: "" };
    if (role === "PM" && pmBlocks.length > 0) {
      base.state_id = String(pmBlocks[0].state_id);
    } else if (role === "PL" && plDistricts.length > 0) {
      base.state_id = String(plDistricts[0].state_id);
    } else if (role === "PO" && userProfile?.state_id) {
      base.state_id = String(userProfile.state_id);
    } else if (role === "SPL") {
      base.state_id = filters.state_id;
    }
    setFilters(base);
    setReportData(null);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const payload = { state_id: Number(filters.state_id) };
      if (filters.district_id) payload.district_id = Number(filters.district_id);
      if (filters.block_id) payload.block_id = Number(filters.block_id);
      if (filters.assessment_year_id) payload.assessment_year_id = filters.assessment_year_id;
      if (filters.cycle_id) payload.cycle_id = filters.cycle_id;

      const blob = await exportQLRReportExcel(payload);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "qlr_report.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setExporting(false);
    }
  };

  const hasFilters = Object.values(filters).some((v) => v !== "");
  const quarters = reportData?.quarters || [];
  const locationName = reportData?.location_name || "";
  const hasData = quarters.some((q) => q.competencies && q.competencies.length > 0);

  const selectCls =
    "h-[34px] border border-[#d0d5dd] rounded-lg px-2.5 text-[12.5px] bg-white focus:outline-none focus:border-[#0078d4] focus:ring-1 focus:ring-[#0078d4]/20 transition-all appearance-none w-full";

  return (
    <div className="p-6">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-[18px] font-bold text-[#24292f] flex items-center gap-2">
            <HiOutlineDocumentReport className="text-[#0078d4]" />
            QLR Report
          </h1>
          <p className="text-[12px] text-[#57606a] mt-0.5">
            Quarterly Learning Report — state/district/block level competency analysis
          </p>
        </div>
        <button
          onClick={handleExport}
          disabled={exporting || !hasData}
          className="inline-flex items-center gap-2 h-[36px] px-4 text-[12.5px] font-medium rounded-lg cursor-pointer bg-[#0078d4] text-white border-none hover:bg-[#106ebe] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <HiOutlineDownload className="text-sm" />
          {exporting ? "Exporting..." : "Export Excel"}
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-[#e5e7eb] mb-5">
        <div className="px-5 py-3 border-b border-[#f0f0f0] flex items-center gap-2">
          <HiOutlineFilter className="text-[#8b949e]" />
          <span className="text-[11px] font-semibold text-[#8b949e] uppercase tracking-wider">
            Filters
          </span>
        </div>
        <div className="px-5 py-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div>
              <label className="block text-[10.5px] font-medium text-[#8b949e] mb-1 uppercase tracking-wide">State</label>
              {(role === "SPL" || role === "PL" || role === "PM" || role === "PO") ? (
                <div className={selectCls + " bg-[#f6f8fa] text-[#57606a] flex items-center"}>
                  {role === "PM"
                    ? (pmBlocks.find(b => String(b.state_id) === String(filters.state_id))?.state_name || (filters.state_id ? `State #${filters.state_id}` : "—"))
                    : role === "PL"
                    ? (plDistricts.find(d => String(d.state_id) === String(filters.state_id))?.state_name || (filters.state_id ? `State #${filters.state_id}` : "—"))
                    : (states.find(s => String(s.id) === filters.state_id)?.name || (filters.state_id ? `State #${filters.state_id}` : "—"))
                  }
                </div>
              ) : (
                <SearchableDropdown
                  value={String(filters.state_id || "")}
                  onChange={(id) => handleFilterChange("state_id", id)}
                  placeholder="Select State"
                  fetchFn={(search, page = 1) => fetchMasterData("states", {
                    page, page_size: 20, search,
                    has_assessment: true,
                    cycle_type: "quarterly",
                  })}
                  variant="report"
                />
              )}
            </div>
            <div>
              <label className="block text-[10.5px] font-medium text-[#8b949e] mb-1 uppercase tracking-wide">Assessment Year</label>
              <select value={filters.assessment_year_id} onChange={(e) => handleFilterChange("assessment_year_id", e.target.value)} className={selectCls} disabled={!filters.state_id}>
                <option value="">Select Assessment Year</option>
                {assessmentYears.map((ay) => (<option key={ay.id} value={ay.id}>{ay.name}</option>))}
              </select>
            </div>
            <div>
              <label className="block text-[10.5px] font-medium text-[#8b949e] mb-1 uppercase tracking-wide">Cycle / Round</label>
              <select value={filters.cycle_id} onChange={(e) => handleFilterChange("cycle_id", e.target.value)} className={selectCls} disabled={!filters.state_id}>
                <option value="">All Cycles</option>
                {cycles.map((c) => (<option key={c.id} value={c.id}>{c.title || `Round ${c.round_number}`}</option>))}
              </select>
            </div>
            <div>
              <label className="block text-[10.5px] font-medium text-[#8b949e] mb-1 uppercase tracking-wide">District</label>
              {(role === "PM" || role === "PL") ? (
                <select value={filters.district_id} onChange={(e) => handleFilterChange("district_id", e.target.value)} className={selectCls} disabled={!filters.state_id}>
                  <option value="">All Districts</option>
                  {(role === "PM"
                    ? [...new Map(pmBlocks.map(b => [b.district_id, {id: b.district_id, name: b.district_name}])).values()]
                    : plDistricts
                  ).slice().sort((a, b) => a.name.localeCompare(b.name)).map((d) => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              ) : (
                <SearchableDropdown
                  key={`qlr-dist-${filters.state_id}`}
                  value={filters.district_id}
                  onChange={(id) => handleFilterChange("district_id", id)}
                  placeholder="All Districts"
                  fetchFn={(search, page = 1) => fetchMasterData("districts", {
                    page, page_size: 20,
                    state_id: Number(filters.state_id),
                    search,
                    has_assessment: true,
                    cycle_type: "quarterly",
                  })}
                  disabled={!filters.state_id}
                  variant="report"
                  emptyMessage="No districts with completed assessments"
                />
              )}
            </div>
            <div>
              <label className="block text-[10.5px] font-medium text-[#8b949e] mb-1 uppercase tracking-wide">Block</label>
              {role === "PM" ? (
                <select value={filters.block_id} onChange={(e) => handleFilterChange("block_id", e.target.value)} className={selectCls} disabled={!filters.state_id}>
                  <option value="">All Blocks</option>
                  {pmBlocks.filter(b => !filters.district_id || String(b.district_id) === String(filters.district_id))
                    .slice().sort((a, b) => a.name.localeCompare(b.name)).map((b) => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                </select>
              ) : (
                <SearchableDropdown
                  key={`qlr-blk-${filters.district_id || filters.state_id}`}
                  value={filters.block_id}
                  onChange={(id) => handleFilterChange("block_id", id)}
                  placeholder="All Blocks"
                  fetchFn={(search, page = 1) => fetchMasterData("blocks", {
                    page, page_size: 20,
                    state_id: Number(filters.state_id),
                    ...(filters.district_id ? { district_id: Number(filters.district_id) } : {}),
                    search,
                    has_assessment: true,
                    cycle_type: "quarterly",
                  })}
                  disabled={!filters.state_id}
                  variant="report"
                  emptyMessage="No blocks with completed assessments"
                />
              )}
            </div>
          </div>
          {hasFilters && (
            <div className="mt-3">
              <button onClick={clearFilters} className="inline-flex items-center gap-1 h-[30px] px-3 text-[11px] font-medium rounded-lg cursor-pointer bg-[#f3f4f6] text-[#57606a] border border-[#d0d5dd] hover:bg-[#e5e7eb] transition-all">
                <HiOutlineX className="text-xs" /> Clear All
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Report Table */}
      {loading ? (
        <div className="bg-white rounded-xl border border-[#e5e7eb] p-10 text-center">
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-[#e5e7eb] rounded w-48 mx-auto" />
            <div className="h-40 bg-[#e5e7eb] rounded" />
          </div>
        </div>
      ) : !filters.state_id ? (
        <div className="bg-white rounded-xl border border-[#e5e7eb] p-10 text-center">
          <HiOutlineAcademicCap className="mx-auto text-3xl text-[#d0d5dd] mb-2" />
          <p className="text-[13px] text-[#57606a]">Select a state to generate the QLR report.</p>
        </div>
      ) : !hasData ? (
        <div className="bg-white rounded-xl border border-[#e5e7eb] p-10 text-center">
          <HiOutlineAcademicCap className="mx-auto text-3xl text-[#d0d5dd] mb-2" />
          <p className="text-[13px] text-[#57606a]">No quarterly cycle data found for the selected filters.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-[#e5e7eb] overflow-hidden">
          <div className="px-4 py-3 border-b border-[#e5e7eb] bg-[#f8f9fa]">
            <h3 className="text-[13px] font-bold text-[#24292f] uppercase tracking-wider">
              {locationName || "QLR Report"}
            </h3>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead>
                <tr className="bg-[#f6f8fa] border-b-2 border-[#d0d5dd]">
                  <th className="text-left px-3 py-3 text-[11px] font-bold text-[#24292f] uppercase tracking-wide w-[120px]">Quarter</th>
                  <th className="text-left px-3 py-3 text-[11px] font-bold text-[#24292f] uppercase tracking-wide">Competency</th>
                  <th className="text-center px-3 py-3 text-[11px] font-bold text-[#24292f] uppercase tracking-wide">Students Tested</th>
                  <th className="text-center px-3 py-3 text-[11px] font-bold text-[#24292f] uppercase tracking-wide">Schools Tested</th>
                  <th className="text-center px-3 py-3 text-[11px] font-bold text-[#24292f] uppercase tracking-wide">Zero Scorers</th>
                  <th className="text-center px-3 py-3 text-[11px] font-bold text-[#24292f] uppercase tracking-wide">Below Cut-off</th>
                  <th className="text-center px-3 py-3 text-[11px] font-bold text-[#24292f] uppercase tracking-wide">Above Cut-off</th>
                  <th className="text-center px-3 py-3 text-[11px] font-bold text-[#24292f] uppercase tracking-wide">Risk Level</th>
                  <th className="text-center px-3 py-3 text-[11px] font-bold text-[#24292f] uppercase tracking-wide">Remediation URL</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#e5e7eb]">
                {quarters.map((qtr) =>
                  qtr.competencies.map((comp, ci) => (
                    <tr key={`${qtr.round_number}-${comp.competency_code}`} className="hover:bg-[#f6f8fa] transition-colors">
                      {ci === 0 ? (
                        <td rowSpan={qtr.competencies.length} className="px-3 py-2 text-[12.5px] font-bold text-[#24292f] align-top border-r border-[#e5e7eb] bg-[#f8f9fa]">
                          {qtr.title}
                        </td>
                      ) : null}
                      <td className="px-3 py-2 text-[12.5px] text-[#24292f]">{comp.competency_name}</td>
                      <td className="px-3 py-2 text-[12.5px] text-[#57606a] text-center">
                        {comp.is_tested ? comp.students_tested : ""}
                      </td>
                      <td className="px-3 py-2 text-[12.5px] text-[#57606a] text-center">
                        {comp.is_tested ? comp.schools_tested : ""}
                      </td>
                      <td className="px-3 py-2 text-[12.5px] text-center">
                        {comp.is_tested ? (
                          <span className="text-[#57606a]">{comp.zero_scorers_pct}%</span>
                        ) : (
                          <span className="text-[#8b949e] italic">Not Tested</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-[12.5px] text-center">
                        {comp.is_tested ? (
                          <span className="text-[#57606a]">{comp.below_cutoff_pct}%</span>
                        ) : (
                          <span className="text-[#8b949e] italic">Not Tested</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-[12.5px] text-center">
                        {comp.is_tested ? (
                          <span className="text-[#57606a]">{comp.above_cutoff_pct}%</span>
                        ) : (
                          <span className="text-[#8b949e] italic">Not Tested</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {(() => {
                          const riskStyle = RISK_COLORS[comp.risk_level] || RISK_COLORS.Low;
                          return (
                            <span className={`inline-block text-[10.5px] font-semibold px-2 py-0.5 rounded-full border ${riskStyle.bg} ${riskStyle.text} ${riskStyle.border}`}>
                              {comp.risk_level}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-3 py-2 text-center text-[11px]">
                        {comp.remediation_url ? (
                          <a href={comp.remediation_url} target="_blank" rel="noopener noreferrer" className="text-[#0078d4] underline truncate max-w-[120px] inline-block">
                            {comp.remediation_url}
                          </a>
                        ) : <span className="text-[#8b949e]">—</span>}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Risk Level Legend */}
          <div className="px-4 py-3 border-t border-[#e5e7eb] bg-[#f8f9fa]">
            <p className="text-[10.5px] text-[#57606a] mb-1 font-semibold">Risk Level Definitions:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
              <p className="text-[10px] text-[#8b949e]">
                <span className="inline-block w-2 h-2 rounded-full bg-green-400 mr-1"></span>
                <strong>Low Risk</strong> = Parent competency has &gt;60% above cut-off
              </p>
              <p className="text-[10px] text-[#8b949e]">
                <span className="inline-block w-2 h-2 rounded-full bg-yellow-400 mr-1"></span>
                <strong>Medium Risk</strong> = Parent competency has 30-60% above cut-off
              </p>
              <p className="text-[10px] text-[#8b949e]">
                <span className="inline-block w-2 h-2 rounded-full bg-orange-400 mr-1"></span>
                <strong>High Risk</strong> = Parent competency has &lt;30% above cut-off
              </p>
              <p className="text-[10px] text-[#8b949e]">
                <span className="inline-block w-2 h-2 rounded-full bg-red-400 mr-1"></span>
                <strong>Ultra High Risk</strong> = &lt;30% above cut-off AND &gt;40% zero scorers
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default QLRReport;
